import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircle as ConfirmIcon,
  DoNotDisturb as DismissIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Close as RemoveIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";

export type ReviewStatus = "confirmed" | "not_an_issue";

export interface ReviewComment {
  id: string;
  text: string;
  authorId?: string;
  authorName: string;
  createdAt: string;
}

export interface ReviewRecord {
  status: ReviewStatus | null;
  statusSetBy: string | null;
  statusSetAt: string | null;
  comments: ReviewComment[];
}

interface DiscrepancyReviewControlsProps {
  review: ReviewRecord | null;
  onStatusChange: (status: ReviewStatus) => void;
  onAddComment: (text: string) => void;
  onRemoveComment?: (commentId: string) => void;
  canRemoveComment?: (comment: ReviewComment) => boolean;
  isUpdating?: boolean;
}

const DiscrepancyReviewControls: React.FC<DiscrepancyReviewControlsProps> = ({
  review,
  onStatusChange,
  onAddComment,
  onRemoveComment,
  canRemoveComment,
  isUpdating = false,
}) => {
  const [editingStatus, setEditingStatus] = useState(false);
  const [newComment, setNewComment] = useState("");

  const hasStatus = review?.status != null;

  const handleSubmitComment = () => {
    if (newComment.trim()) {
      onAddComment(newComment.trim());
      setNewComment("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  return (
    <Box>
      {/* Status Section */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 1.5 }}>
          Review Status
        </Typography>

        {hasStatus && !editingStatus ? (
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {review.status === "confirmed" ? (
                <Chip
                  icon={<ConfirmIcon sx={{ fontSize: 14 }} />}
                  label="Confirmed Issue"
                  size="small"
                  sx={{
                    bgcolor: tokens.status.dangerBg,
                    color: tokens.status.danger,
                    fontWeight: tokens.fontWeight.bold,
                    fontSize: tokens.fontSize["2xs"],
                    "& .MuiChip-icon": { color: tokens.status.danger },
                  }}
                />
              ) : (
                <Chip
                  icon={<DismissIcon sx={{ fontSize: 14 }} />}
                  label="Not an Issue"
                  size="small"
                  sx={{
                    bgcolor: "#D1FAE5",
                    color: tokens.status.success,
                    fontWeight: tokens.fontWeight.bold,
                    fontSize: tokens.fontSize["2xs"],
                    "& .MuiChip-icon": { color: tokens.status.success },
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => setEditingStatus(true)}
                sx={{ color: tokens.text.secondary }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
            <Typography variant="caption" sx={{ color: tokens.text.secondary, mt: 0.5, display: "block" }}>
              Set by {review.statusSetBy} on{" "}
              {review.statusSetAt
                ? new Date(review.statusSetAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : ""}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="contained"
                size="small"
                disabled={isUpdating}
                startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <ConfirmIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  onStatusChange("confirmed");
                  setEditingStatus(false);
                }}
                sx={{
                  fontWeight: tokens.fontWeight.bold,
                  fontSize: tokens.fontSize.sm2,
                  borderRadius: tokens.radius.md,
                  px: 2.5,
                  py: 0.75,
                  bgcolor: tokens.status.success,
                  "&:hover": { bgcolor: "#047857" },
                }}
              >
                Confirm Issue
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={isUpdating}
                startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <DismissIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  onStatusChange("not_an_issue");
                  setEditingStatus(false);
                }}
                sx={{
                  fontWeight: tokens.fontWeight.bold,
                  fontSize: tokens.fontSize.sm2,
                  borderRadius: tokens.radius.md,
                  px: 2.5,
                  py: 0.75,
                  bgcolor: tokens.status.danger,
                  "&:hover": { bgcolor: tokens.status.dangerHover },
                }}
              >
                Not an Issue
              </Button>
              {hasStatus && !isUpdating && (
                <Button
                  size="small"
                  onClick={() => setEditingStatus(false)}
                  sx={{ color: tokens.text.secondary }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Comments Section */}
      <Box>
        <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 1.5 }}>
          Comments
        </Typography>

        {/* Existing Comments */}
        {review?.comments && review.comments.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {review.comments.map((comment) => (
              <Box
                key={comment.id}
                sx={{
                  p: 1.5,
                  mb: 1,
                  bgcolor: tokens.bg.hover,
                  borderRadius: tokens.radius.sm,
                  borderLeft: `3px solid ${tokens.brand.primary}`,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}
                  >
                    {comment.authorName}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" sx={{ color: tokens.text.muted }}>
                      {new Date(comment.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </Typography>
                    {onRemoveComment && (!canRemoveComment || canRemoveComment(comment)) && (
                      <IconButton
                        size="small"
                        onClick={() => onRemoveComment(comment.id)}
                        sx={{
                          p: 0.25,
                          color: tokens.text.muted,
                          "&:hover": { color: tokens.status.danger },
                        }}
                      >
                        <RemoveIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
                <Typography variant="body2" sx={{ color: tokens.text.body }}>
                  {comment.text}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Add Comment Input */}
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            multiline
            minRows={1}
            maxRows={3}
            fullWidth
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isUpdating}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fff",
                borderRadius: "24px",
                px: 1,
              },
            }}
          />
          <IconButton
            onClick={handleSubmitComment}
            disabled={!newComment.trim() || isUpdating}
            sx={{
              color: newComment.trim() && !isUpdating ? tokens.text.muted : tokens.text.disabled,
              "&:hover": { color: tokens.text.secondary },
              "&.Mui-disabled": { color: tokens.text.disabled },
            }}
          >
            {isUpdating ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
};

export default DiscrepancyReviewControls;
